import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { money } from "../lib/api";

const STATUS_LABEL = { pending: "Pendente", paid: "Pago", shipped: "Enviado", delivered: "Entregue", cancelled: "Cancelado" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [tracking, setTracking] = useState("");

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/orders${q}`).then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (status) => {
    await api.put(`/admin/orders/${detail.id}`, { status, tracking_code: tracking || undefined });
    toast.success("Pedido atualizado");
    setDetail(null);
    load();
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <h1 className="font-display text-3xl tracking-tight">Pedidos</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-[#141414] border-white/10" data-testid="orders-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="paid">Pagos</SelectItem>
            <SelectItem value="shipped">Enviados</SelectItem>
            <SelectItem value="delivered">Entregues</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="orders-table">
          <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Nº</th>
              <th className="text-left px-5 py-3">Data</th>
              <th className="text-left px-5 py-3">Cliente</th>
              <th className="text-left px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`order-row-${o.id}`}>
                <td className="px-5 py-3.5 font-medium" data-testid="order-number-cell">{o.order_number ? `#${String(o.order_number).padStart(4, "0")}` : "—"}</td>
                <td className="px-5 py-3.5 text-zinc-400">{new Date(o.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-5 py-3.5 text-zinc-300">{o.customer?.name}<span className="block text-xs text-zinc-600">{o.customer?.email}</span></td>
                <td className="px-5 py-3.5 text-gold">{money(o.total)}</td>
                <td className="px-5 py-3.5">
                  <span className={`text-xs px-2 py-1 rounded ${
                    o.status === "paid" ? "bg-emerald-500/10 text-emerald-400" :
                    o.status === "pending" ? "bg-amber-500/10 text-amber-400" :
                    o.status === "cancelled" ? "bg-red-500/10 text-red-400" : "bg-sky-500/10 text-sky-400"
                  }`} data-testid="order-status-badge">{STATUS_LABEL[o.status] || o.status}</span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <Button size="sm" variant="outline" className="border-white/15 text-zinc-300 hover:text-gold"
                    onClick={() => { setDetail(o); setTracking(o.tracking_code || ""); }} data-testid="order-detail-button">
                    Detalhes
                  </Button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-600">Nenhum pedido.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Pedido {detail?.order_number ? `#${String(detail.order_number).padStart(4, "0")}` : "(pendente)"}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="label-caps mb-2">Cliente</p>
                  <p className="text-zinc-200">{detail.customer?.name}</p>
                  <p className="text-zinc-500 text-xs">{detail.customer?.email} {detail.customer?.phone && `· ${detail.customer.phone}`}</p>
                </div>
                <div>
                  <p className="label-caps mb-2">Pagamento</p>
                  <p className="text-zinc-200">{STATUS_LABEL[detail.status]}</p>
                  {detail.transaction_nsu && <p className="text-zinc-600 text-xs font-mono">NSU: {detail.transaction_nsu}</p>}
                  {detail.originality_code && <p className="text-gold text-xs font-mono mt-1" data-testid="detail-originality">{detail.originality_code}</p>}
                </div>
              </div>
              {detail.address && (
                <div>
                  <p className="label-caps mb-2">Entrega</p>
                  <p className="text-zinc-300 text-xs">
                    {detail.address.street}, {detail.address.number} {detail.address.complement} — {detail.address.city}/{detail.address.state} — CEP {detail.address.cep}
                  </p>
                </div>
              )}
              <div>
                <p className="label-caps mb-2">Itens</p>
                {detail.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between py-1.5 border-b border-white/5">
                    <span className="text-zinc-300">{i.quantity}× {i.name} <span className="text-zinc-600">({i.variation_name} · {i.sku})</span></span>
                    <span className="text-zinc-200">{money(i.unit_price * i.quantity)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3"><span className="text-zinc-400">Total (frete {money(detail.shipping)})</span><span className="text-gold font-semibold">{money(detail.total)}</span></div>
              </div>
              <div>
                <Label>Código de rastreio</Label>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="tracking-input"
                  className="mt-1.5 bg-[#141414] border-white/10" placeholder="Opcional" />
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.status === "paid" && (
                  <Button asChild size="sm" variant="outline" className="border-gold/40 text-gold hover:bg-amber-500/10" data-testid="order-pdf-button">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${detail.id}/nota.pdf`} target="_blank" rel="noopener noreferrer">Nota PDF</a>
                  </Button>
                )}
                {["paid", "shipped", "delivered", "cancelled"].map((s) => (
                  <Button key={s} size="sm" variant={detail.status === s ? "default" : "outline"}
                    onClick={() => updateStatus(s)} data-testid={`set-status-${s}`}
                    className={detail.status === s ? "bg-gold text-black" : "border-white/15 text-zinc-300 hover:text-gold"}>
                    {STATUS_LABEL[s]}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
